import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";
import "./AdminPrivacyGovernance.css";

type View="OVERVIEW"|"SCHOOLS"|"FINDINGS"|"RETENTION"|"AUDIT";

type Check={
  key:string;
  label:string;
  status:"PASS"|"ATTENTION"|"BLOCKED"|"INFO"|string;
  detail:string;
};

type SchoolRow={
  schoolId:string;
  schoolName:string;
  schoolCode?:string|null;
  privacyState:"READY"|"ATTENTION"|"BLOCKED"|string;
  activeStudents:number;
  learnersWithGuardian:number;
  learnersWithoutGuardian:number;
  guardianCoverageRate?:number|null;
  publishedPurposeCount:number;
  pendingConsents:number;
  expiredPendingConsents:number;
  activeConsents:number;
  expiringConsents:number;
  withdrawnConsents:number;
  activeShares:number;
  expiringShares:number;
  staleActiveShares:number;
  shareAccesses30d:number;
  accessAfterRevoke:number;
  invalidShareContracts:number;
};

type Purpose={
  definitionId:string;
  schoolId:string;
  schoolName:string;
  purposeKey:string;
  purposeVersion:number;
  status:string;
  title:string;
  allowedCategories:string[];
  allowedRecipientTypes:string[];
  defaultValidDays:number;
  publishedAt:string;
  retiredAt?:string|null;
};

type RetentionPolicy={
  recordType:string;
  label:string;
  configured:boolean;
  policyId?:string|null;
  retentionDays?:number|null;
  status?:string|null;
  policyNote?:string|null;
  updatedAt?:string|null;
};

type Finding={
  findingKey:string;
  severity:"HIGH"|"ATTENTION"|string;
  domain:string;
  title:string;
  detail:string;
  schoolId?:string|null;
  schoolName?:string|null;
  occurredAt:string;
  suggestedRoute?:string|null;
  metadata?:Record<string,unknown>;
  reviewStatus:"OPEN"|"ACKNOWLEDGED"|"RESOLVED"|"EXEMPT"|string;
  reviewNote?:string|null;
  reviewedAt?:string|null;
};

type AuditRow={
  auditId:string;
  occurredAt:string;
  correlationId?:string|null;
  actionKey:string;
  resourceType:string;
  resourceId?:string|null;
  result:string;
  actorName?:string|null;
  organizationName?:string|null;
};

type Governance={
  scope:{
    rootOrganizationId:string;
    rootName:string;
    rootType:string;
    expiringDays:number;
  };
  summary:{
    schoolCount:number;
    activeStudents:number;
    learnersWithGuardian:number;
    guardianCoverageRate?:number|null;
    publishedPurposeSchools:number;
    pendingConsents:number;
    activeConsents:number;
    expiringConsents:number;
    activeShares:number;
    expiringShares:number;
    shareAccesses30d:number;
    retentionConfigured:number;
    retentionRequired:number;
    openFindings:number;
    highOpenFindings:number;
  };
  checks:Check[];
  schools:SchoolRow[];
  purposes:Purpose[];
  retentionPolicies:RetentionPolicy[];
  findings:Finding[];
  privacyAudit:AuditRow[];
};

type Draft={days:string;note:string};

function formatDate(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function rate(value?:number|null){
  return value==null ? "N/A" : value.toLocaleString("ko-KR",{maximumFractionDigits:1})+"%";
}

function stateLabel(value:string){
  if(value==="READY")return "정상";
  if(value==="ATTENTION")return "확인 필요";
  if(value==="BLOCKED")return "우선 조치";
  return value;
}

function reviewLabel(value:string){
  const map:Record<string,string>={
    OPEN:"미처리",
    ACKNOWLEDGED:"확인 중",
    RESOLVED:"해결",
    EXEMPT:"예외"
  };
  return map[value] || value;
}

function categoryLabel(value:string){
  const map:Record<string,string>={
    PROFILE:"기본정보",
    CAREER_SUMMARY:"진로 요약",
    ASSESSMENT_SUMMARY:"검사 요약",
    ACTIVITY_SUMMARY:"활동 요약",
    EVIDENCE_SUMMARY:"증빙 요약",
    CREDENTIAL_SUMMARY:"배지 요약"
  };
  return map[value] || value;
}

function recipientLabel(value:string){
  const map:Record<string,string>={
    GUARDIAN:"보호자",
    SCHOOL:"학교",
    EDUCATION_OFFICE:"교육청",
    EXTERNAL_INSTITUTION:"외부기관",
    OTHER:"기타"
  };
  return map[value] || value;
}

function navigate(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function AdminPrivacyGovernance({
  client,
  rootOrganizationId
}:{client:PlatformClient;rootOrganizationId:string}){
  const [view,setView]=useState<View>("OVERVIEW");
  const [data,setData]=useState<Governance|null>(null);
  const [expiringDays,setExpiringDays]=useState(30);
  const [selectedSchoolId,setSelectedSchoolId]=useState("");
  const [selectedFindingKey,setSelectedFindingKey]=useState("");
  const [schoolQuery,setSchoolQuery]=useState("");
  const [schoolState,setSchoolState]=useState("ALL");
  const [findingStatus,setFindingStatus]=useState("OPEN");
  const [findingDomain,setFindingDomain]=useState("ALL");
  const [reviewNote,setReviewNote]=useState("");
  const [drafts,setDrafts]=useState<Record<string,Draft>>({});
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [refresh,setRefresh]=useState(0);

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("admin_privacy_governance",{
      p_root_organization_id:rootOrganizationId,
      p_expiring_days:expiringDays
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Governance;
      setData(next);
      setSelectedSchoolId((current)=>
        next.schools.some((item)=>item.schoolId===current)
          ? current
          : next.schools[0]?.schoolId || ""
      );
      setSelectedFindingKey((current)=>
        next.findings.some((item)=>item.findingKey===current)
          ? current
          : next.findings.find((item)=>item.reviewStatus==="OPEN")?.findingKey
            || next.findings[0]?.findingKey
            || ""
      );
      setDrafts((current)=>{
        const copy={...current};
        next.retentionPolicies.forEach((item)=>{
          if(!copy[item.recordType]){
            copy[item.recordType]={
              days:item.retentionDays!=null?String(item.retentionDays):"",
              note:item.policyNote || ""
            };
          }else if(item.configured){
            copy[item.recordType]={
              days:String(item.retentionDays ?? ""),
              note:item.policyNote || ""
            };
          }
        });
        return copy;
      });
    });
    return()=>{active=false;};
  },[client,rootOrganizationId,expiringDays,refresh]);

  const selectedSchool=useMemo(
    ()=>data?.schools.find((item)=>item.schoolId===selectedSchoolId) || null,
    [data,selectedSchoolId]
  );

  const selectedFinding=useMemo(
    ()=>data?.findings.find((item)=>item.findingKey===selectedFindingKey) || null,
    [data,selectedFindingKey]
  );

  useEffect(()=>{
    setReviewNote(selectedFinding?.reviewNote || "");
  },[selectedFinding?.findingKey,selectedFinding?.reviewedAt]);

  const visibleSchools=useMemo(()=>{
    const keyword=schoolQuery.trim().toLowerCase();
    return (data?.schools || []).filter((item)=>{
      if(schoolState!=="ALL" && item.privacyState!==schoolState)return false;
      return !keyword || [item.schoolName,item.schoolCode || ""]
        .some((value)=>value.toLowerCase().includes(keyword));
    });
  },[data,schoolQuery,schoolState]);

  const findingDomains=useMemo(
    ()=>["ALL",...Array.from(new Set((data?.findings || []).map((item)=>item.domain)))],
    [data]
  );

  const visibleFindings=useMemo(()=>{
    return (data?.findings || []).filter((item)=>{
      if(findingStatus!=="ALL" && item.reviewStatus!==findingStatus)return false;
      if(findingDomain!=="ALL" && item.domain!==findingDomain)return false;
      return true;
    });
  },[data,findingStatus,findingDomain]);

  const publishedPurposes=useMemo(
    ()=>(data?.purposes || []).filter((item)=>item.status==="PUBLISHED"),
    [data]
  );

  async function reviewFinding(status:"ACKNOWLEDGED"|"RESOLVED"|"EXEMPT"){
    if(!selectedFinding)return;
    setBusy("finding:"+status);
    setError("");
    setMessage("");

    const {error}=await client.rpc("admin_review_privacy_finding",{
      p_root_organization_id:rootOrganizationId,
      p_finding_key:selectedFinding.findingKey,
      p_status:status,
      p_note:reviewNote.trim() || null,
      p_source_observed_at:selectedFinding.occurredAt
    });

    setBusy("");
    if(error){setError(error.message);return;}
    setMessage(
      status==="ACKNOWLEDGED"?"Finding을 확인 중으로 표시했습니다."
        : status==="RESOLVED"?"Finding을 해결 처리했습니다."
          :"Finding을 정책상 예외로 표시했습니다."
    );
    setRefresh((value)=>value+1);
  }

  async function saveRetention(policy:RetentionPolicy){
    const draft=drafts[policy.recordType];
    const days=Number(draft?.days || "");
    if(!Number.isInteger(days) || days<1 || days>3650){
      setError("보존기간은 1~3650일 범위의 정수로 입력하세요.");
      return;
    }

    setBusy("retention:"+policy.recordType);
    setError("");
    setMessage("");

    const {error}=await client.rpc("admin_set_privacy_retention_policy",{
      p_root_organization_id:rootOrganizationId,
      p_record_type:policy.recordType,
      p_retention_days:days,
      p_policy_note:draft?.note.trim() || null
    });

    setBusy("");
    if(error){setError(error.message);return;}
    setMessage(policy.label+" 보존정책을 저장했습니다.");
    setRefresh((value)=>value+1);
  }

  if(!data)return <p className="muted">개인정보 거버넌스 현황을 불러오는 중…</p>;

  const s=data.summary;
  const posture=s.highOpenFindings>0?"BLOCKED":s.openFindings>0?"ATTENTION":"PASS";

  return (
    <section className="privacy-governance-page">
      <header className="privacy-governance-hero">
        <div>
          <p className="eyebrow">PRIVACY, CONSENT & DATA GOVERNANCE CENTER</p>
          <h2>개인정보·동의·데이터 거버넌스</h2>
          <p>
            보호자 동의와 최소정보 공유를 학교 단위로 관제합니다.
            학생·보호자 원문 데이터는 상위기관 화면에 노출하지 않고 정책, 집계, 만료, 철회, Audit 상태만 확인합니다.
          </p>
        </div>
        <div className={"privacy-posture "+posture.toLowerCase()}>
          <span>PRIVACY POSTURE</span>
          <strong>{posture==="PASS"?"정상":posture==="BLOCKED"?"우선 조치":"정비 필요"}</strong>
          <small>미처리 {s.openFindings} · High {s.highOpenFindings}</small>
        </div>
      </header>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      <div className="privacy-kpis">
        <PrivacyKpi label="학생" value={s.activeStudents} />
        <PrivacyKpi label="보호자 커버리지" value={rate(s.guardianCoverageRate)} sub={s.learnersWithGuardian+"/"+s.activeStudents+"명"} tone={s.guardianCoverageRate!=null && s.guardianCoverageRate<100?"warn":"normal"} />
        <PrivacyKpi label="목적정책 게시 학교" value={s.publishedPurposeSchools} sub={s.schoolCount+"개교 중"} />
        <PrivacyKpi label="유효 동의" value={s.activeConsents} sub={"대기 "+s.pendingConsents} />
        <PrivacyKpi label="활성 공유" value={s.activeShares} sub={"만료예정 "+s.expiringShares} />
        <PrivacyKpi label="30일 공유 접근" value={s.shareAccesses30d} />
        <PrivacyKpi label="보존정책" value={s.retentionConfigured+"/"+s.retentionRequired} tone={s.retentionConfigured<s.retentionRequired?"warn":"normal"} />
      </div>

      <nav className="privacy-tabs">
        <button className={view==="OVERVIEW"?"active":""} onClick={()=>setView("OVERVIEW")}>Privacy Readiness</button>
        <button className={view==="SCHOOLS"?"active":""} onClick={()=>setView("SCHOOLS")}>학교 거버넌스</button>
        <button className={view==="FINDINGS"?"active":""} onClick={()=>setView("FINDINGS")}>Finding {s.openFindings || ""}</button>
        <button className={view==="RETENTION"?"active":""} onClick={()=>setView("RETENTION")}>보존정책</button>
        <button className={view==="AUDIT"?"active":""} onClick={()=>setView("AUDIT")}>Privacy Audit</button>
      </nav>

      <div className="privacy-window">
        <span>만료예정 기준</span>
        {[7,30,60,90].map((days)=>(
          <button key={days} className={expiringDays===days?"active":""} onClick={()=>setExpiringDays(days)}>{days}일</button>
        ))}
      </div>

      {view==="OVERVIEW" && (
        <div className="privacy-overview-grid">
          <section className="privacy-card">
            <div className="privacy-section-head">
              <div><p className="eyebrow">PRIVACY CONTROLS</p><h3>핵심 보호장치</h3></div>
              <span>{data.checks.length} checks</span>
            </div>
            <div className="privacy-check-list">
              {data.checks.map((item)=>(
                <article key={item.key}>
                  <span className={"privacy-check "+item.status.toLowerCase()}>{item.status==="PASS"?"정상":item.status==="BLOCKED"?"조치":item.status==="ATTENTION"?"확인":"정보"}</span>
                  <div><strong>{item.label}</strong><small>{item.detail}</small></div>
                </article>
              ))}
            </div>
          </section>

          <section className="privacy-card">
            <div className="privacy-section-head">
              <div><p className="eyebrow">CONSENT → SHARE</p><h3>공유 거버넌스 체인</h3></div>
            </div>
            <div className="privacy-chain">
              <ChainStep label="Purpose" detail="학교 승인 목적정책" />
              <ChainStep label="Guardian" detail="보호자 연락처" />
              <ChainStep label="Consent" detail="명시적 동의" />
              <ChainStep label="Projection" detail="최소정보 Snapshot" />
              <ChainStep label="Share" detail="기간제 공유" />
              <ChainStep label="Audit" detail="열람·철회·회수 이력" />
            </div>
            <p className="privacy-boundary-note">
              시스템은 특정 법 조항이나 연령기준을 코드에 고정하지 않습니다. 실제 적용 근거와 절차는 학교의 게시된 Purpose Definition과 기관 정책으로 관리합니다.
            </p>
          </section>

          <section className="privacy-card privacy-purpose-card">
            <div className="privacy-section-head">
              <div><p className="eyebrow">PURPOSE DEFINITIONS</p><h3>게시된 목적정책</h3></div>
              <span>{publishedPurposes.length}개</span>
            </div>
            <div className="privacy-purpose-list">
              {publishedPurposes.map((item)=>(
                <article key={item.definitionId}>
                  <div>
                    <strong>{item.title}</strong>
                    <small>{item.schoolName} · {item.purposeKey} v{item.purposeVersion}</small>
                  </div>
                  <div className="purpose-tags">
                    {item.allowedCategories.map((value)=><span key={value}>{categoryLabel(value)}</span>)}
                  </div>
                  <div className="purpose-recipients">
                    {item.allowedRecipientTypes.map((value)=><span key={value}>{recipientLabel(value)}</span>)}
                  </div>
                  <b>{item.defaultValidDays}일</b>
                </article>
              ))}
              {!publishedPurposes.length && <p className="case-empty">현재 게시된 Consent Purpose Definition이 없습니다.</p>}
            </div>
          </section>

          <section className="privacy-card">
            <div className="privacy-section-head">
              <div><p className="eyebrow">CURRENT ACTIONS</p><h3>현재 정비 항목</h3></div>
              <button className="text-button" onClick={()=>setView("FINDINGS")}>전체 Finding →</button>
            </div>
            <div className="privacy-action-summary">
              <div><span>미처리 Finding</span><strong>{s.openFindings}</strong></div>
              <div><span>High</span><strong>{s.highOpenFindings}</strong></div>
              <div><span>만료예정 동의</span><strong>{s.expiringConsents}</strong></div>
              <div><span>만료예정 공유</span><strong>{s.expiringShares}</strong></div>
            </div>
          </section>
        </div>
      )}

      {view==="SCHOOLS" && (
        <>
          <div className="privacy-school-toolbar">
            <input value={schoolQuery} onChange={(event)=>setSchoolQuery(event.target.value)} placeholder="학교명·코드 검색" />
            <select value={schoolState} onChange={(event)=>setSchoolState(event.target.value)}>
              <option value="ALL">전체 상태</option>
              <option value="BLOCKED">우선 조치</option>
              <option value="ATTENTION">확인 필요</option>
              <option value="READY">정상</option>
            </select>
          </div>

          <div className="privacy-school-layout">
            <aside className="privacy-school-list">
              <div className="panel-title"><strong>학교 Privacy Inbox</strong><span>{visibleSchools.length}개교</span></div>
              {visibleSchools.map((school)=>(
                <button key={school.schoolId} className={selectedSchoolId===school.schoolId?"active":""} onClick={()=>setSelectedSchoolId(school.schoolId)}>
                  <div>
                    <span className={"privacy-school-state "+school.privacyState.toLowerCase()}>{stateLabel(school.privacyState)}</span>
                    <span>{rate(school.guardianCoverageRate)}</span>
                  </div>
                  <strong>{school.schoolName}</strong>
                  <p>학생 {school.activeStudents} · 동의 {school.activeConsents} · 공유 {school.activeShares}</p>
                  <small>Purpose {school.publishedPurposeCount} · 30일 열람 {school.shareAccesses30d}</small>
                </button>
              ))}
              {!visibleSchools.length && <p className="case-empty">조건에 맞는 학교가 없습니다.</p>}
            </aside>

            <main className="privacy-school-detail">
              {selectedSchool ? (
                <>
                  <header>
                    <div>
                      <span className={"privacy-school-state "+selectedSchool.privacyState.toLowerCase()}>{stateLabel(selectedSchool.privacyState)}</span>
                      <h2>{selectedSchool.schoolName}</h2>
                      <p>{selectedSchool.schoolCode || "학교코드 없음"}</p>
                    </div>
                    <button className="secondary" onClick={()=>navigate("/organizations?organization="+encodeURIComponent(selectedSchool.schoolId))}>기관 360</button>
                  </header>

                  <div className="privacy-school-metrics">
                    <SchoolMetric label="학생" value={selectedSchool.activeStudents} />
                    <SchoolMetric label="보호자 등록" value={selectedSchool.learnersWithGuardian} />
                    <SchoolMetric label="보호자 미등록" value={selectedSchool.learnersWithoutGuardian} attention={selectedSchool.learnersWithoutGuardian>0} />
                    <SchoolMetric label="유효 동의" value={selectedSchool.activeConsents} />
                    <SchoolMetric label="활성 공유" value={selectedSchool.activeShares} />
                    <SchoolMetric label="30일 열람" value={selectedSchool.shareAccesses30d} />
                  </div>

                  <div className="privacy-school-two">
                    <section className="privacy-card">
                      <div className="privacy-section-head"><div><p className="eyebrow">CONSENT</p><h3>동의 상태</h3></div></div>
                      <div className="privacy-line-list">
                        <Line label="게시 Purpose" value={selectedSchool.publishedPurposeCount} attention={selectedSchool.publishedPurposeCount===0} />
                        <Line label="동의 대기" value={selectedSchool.pendingConsents} />
                        <Line label="만료된 PENDING" value={selectedSchool.expiredPendingConsents} attention={selectedSchool.expiredPendingConsents>0} />
                        <Line label="유효 동의" value={selectedSchool.activeConsents} />
                        <Line label={expiringDays+"일 내 만료"} value={selectedSchool.expiringConsents} attention={selectedSchool.expiringConsents>0} />
                        <Line label="철회 동의" value={selectedSchool.withdrawnConsents} />
                      </div>
                    </section>

                    <section className="privacy-card">
                      <div className="privacy-section-head"><div><p className="eyebrow">DATA SHARE</p><h3>공유 상태</h3></div></div>
                      <div className="privacy-line-list">
                        <Line label="활성 공유" value={selectedSchool.activeShares} />
                        <Line label={expiringDays+"일 내 만료"} value={selectedSchool.expiringShares} attention={selectedSchool.expiringShares>0} />
                        <Line label="유효하지 않은 ACTIVE" value={selectedSchool.staleActiveShares} attention={selectedSchool.staleActiveShares>0} />
                        <Line label="회수 후 접근" value={selectedSchool.accessAfterRevoke} attention={selectedSchool.accessAfterRevoke>0} />
                        <Line label="공유 계약 불일치" value={selectedSchool.invalidShareContracts} attention={selectedSchool.invalidShareContracts>0} />
                        <Line label="30일 접근" value={selectedSchool.shareAccesses30d} />
                      </div>
                    </section>
                  </div>
                </>
              ):<p className="case-empty">학교를 선택하세요.</p>}
            </main>
          </div>
        </>
      )}

      {view==="FINDINGS" && (
        <>
          <div className="privacy-finding-toolbar">
            <select value={findingStatus} onChange={(event)=>setFindingStatus(event.target.value)}>
              <option value="ALL">전체 상태</option>
              <option value="OPEN">미처리</option>
              <option value="ACKNOWLEDGED">확인 중</option>
              <option value="RESOLVED">해결</option>
              <option value="EXEMPT">예외</option>
            </select>
            <select value={findingDomain} onChange={(event)=>setFindingDomain(event.target.value)}>
              {findingDomains.map((item)=><option key={item} value={item}>{item==="ALL"?"전체 영역":item}</option>)}
            </select>
          </div>

          <div className="privacy-finding-layout">
            <aside className="privacy-finding-list">
              <div className="panel-title"><strong>Privacy Finding</strong><span>{visibleFindings.length}건</span></div>
              {visibleFindings.map((item)=>(
                <button key={item.findingKey} className={selectedFindingKey===item.findingKey?"active":""} onClick={()=>setSelectedFindingKey(item.findingKey)}>
                  <div>
                    <span className={"privacy-severity "+item.severity.toLowerCase()}>{item.severity}</span>
                    <span className={"privacy-review "+item.reviewStatus.toLowerCase()}>{reviewLabel(item.reviewStatus)}</span>
                  </div>
                  <strong>{item.title}</strong>
                  <p>{item.schoolName || item.domain}</p>
                  <small>{formatDate(item.occurredAt)}</small>
                </button>
              ))}
              {!visibleFindings.length && <p className="case-empty">조건에 맞는 Finding이 없습니다.</p>}
            </aside>

            <main className="privacy-finding-detail">
              {selectedFinding ? (
                <>
                  <header>
                    <div>
                      <div className="privacy-detail-tags">
                        <span className={"privacy-severity "+selectedFinding.severity.toLowerCase()}>{selectedFinding.severity}</span>
                        <span>{selectedFinding.domain}</span>
                        <span>{reviewLabel(selectedFinding.reviewStatus)}</span>
                      </div>
                      <h2>{selectedFinding.title}</h2>
                      <p>{selectedFinding.detail}</p>
                    </div>
                  </header>

                  <div className="privacy-finding-meta">
                    <Meta label="학교" value={selectedFinding.schoolName || "Scope 전체"} />
                    <Meta label="관찰 시각" value={formatDate(selectedFinding.occurredAt)} />
                    <Meta label="Finding Key" value={selectedFinding.findingKey} />
                  </div>

                  {selectedFinding.metadata && Object.keys(selectedFinding.metadata).length>0 && (
                    <section className="privacy-card">
                      <div className="privacy-section-head"><div><h3>탐지 근거</h3></div></div>
                      <div className="privacy-metadata-grid">
                        {Object.entries(selectedFinding.metadata).map(([key,value])=>(
                          <div key={key}><span>{key}</span><strong>{String(value ?? "-")}</strong></div>
                        ))}
                      </div>
                    </section>
                  )}

                  <section className="privacy-card privacy-review-card">
                    <div className="privacy-section-head">
                      <div><p className="eyebrow">GOVERNANCE REVIEW</p><h3>관리자 검토</h3></div>
                      <span>{selectedFinding.reviewedAt?formatDate(selectedFinding.reviewedAt):"미검토"}</span>
                    </div>
                    <textarea rows={4} value={reviewNote} onChange={(event)=>setReviewNote(event.target.value)} placeholder="확인 내용, 기관 정책, 후속 조치 또는 예외 사유" />
                    <div className="privacy-review-actions">
                      {selectedFinding.suggestedRoute && <button className="secondary" onClick={()=>navigate(selectedFinding.suggestedRoute || "/privacy-governance")}>관련 화면</button>}
                      <span />
                      <button className="secondary" disabled={busy!==""} onClick={()=>reviewFinding("EXEMPT")}>정책상 예외</button>
                      <button className="secondary" disabled={busy!==""} onClick={()=>reviewFinding("ACKNOWLEDGED")}>확인 중</button>
                      <button className="primary" disabled={busy!==""} onClick={()=>reviewFinding("RESOLVED")}>해결 처리</button>
                    </div>
                  </section>
                </>
              ):<p className="case-empty">Finding을 선택하세요.</p>}
            </main>
          </div>
        </>
      )}

      {view==="RETENTION" && (
        <section className="privacy-card">
          <div className="privacy-section-head">
            <div>
              <p className="eyebrow">RETENTION POLICY REGISTER</p>
              <h3>기록유형별 보존정책</h3>
              <p>법정·기관 기준을 확인한 뒤 기간을 입력하세요. 이 설정은 정책 선언과 Audit을 위한 것이며 자동 삭제를 실행하지 않습니다.</p>
            </div>
            <span>{s.retentionConfigured}/{s.retentionRequired} 설정</span>
          </div>

          <div className="retention-policy-list">
            {data.retentionPolicies.map((policy)=>{
              const draft=drafts[policy.recordType] || {days:"",note:""};
              return (
                <article key={policy.recordType}>
                  <div className="retention-title">
                    <span className={policy.configured?"configured":"missing"}>{policy.configured?"설정됨":"미설정"}</span>
                    <div><strong>{policy.label}</strong><small>{policy.recordType}</small></div>
                  </div>
                  <label>보존기간(일)
                    <input
                      type="number"
                      min="1"
                      max="3650"
                      value={draft.days}
                      onChange={(event)=>setDrafts((current)=>({
                        ...current,
                        [policy.recordType]:{...draft,days:event.target.value}
                      }))}
                      placeholder="기관 기준 입력"
                    />
                  </label>
                  <label>정책 메모
                    <input
                      value={draft.note}
                      onChange={(event)=>setDrafts((current)=>({
                        ...current,
                        [policy.recordType]:{...draft,note:event.target.value}
                      }))}
                      placeholder="근거 문서·정책명·검토 메모"
                    />
                  </label>
                  <div className="retention-save">
                    <small>{policy.updatedAt?"최근 변경 "+formatDate(policy.updatedAt):"아직 정책이 없습니다."}</small>
                    <button className="primary" disabled={busy!==""} onClick={()=>saveRetention(policy)}>
                      {busy==="retention:"+policy.recordType?"저장 중…":"정책 저장"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {view==="AUDIT" && (
        <section className="privacy-card">
          <div className="privacy-section-head">
            <div><p className="eyebrow">PRIVACY AUDIT</p><h3>동의·공유 민감 작업 이력</h3></div>
            <span>{data.privacyAudit.length}건</span>
          </div>
          <div className="privacy-audit-table">
            <div className="privacy-audit-row head"><span>시각</span><span>작업</span><span>대상</span><span>Actor</span><span>기관</span><span>결과</span><span>Trace</span></div>
            {data.privacyAudit.map((item)=>(
              <div className="privacy-audit-row" key={item.auditId}>
                <span>{formatDate(item.occurredAt)}</span>
                <strong>{item.actionKey}</strong>
                <span>{item.resourceType}{item.resourceId?" · "+item.resourceId:""}</span>
                <span>{item.actorName || "-"}</span>
                <span>{item.organizationName || "-"}</span>
                <span className={item.result==="SUCCESS"?"audit-good":"audit-bad"}>{item.result}</span>
                <button disabled={!item.correlationId} onClick={()=>navigate("/audit")}>Trace</button>
              </div>
            ))}
            {!data.privacyAudit.length && <p className="case-empty">Privacy 관련 Audit 이력이 없습니다.</p>}
          </div>
        </section>
      )}
    </section>
  );
}

function PrivacyKpi({
  label,value,sub,tone="normal"
}:{label:string;value:string|number;sub?:string;tone?:"normal"|"warn"}){
  return <div className={"privacy-kpi "+tone}><span>{label}</span><strong>{value}</strong>{sub&&<small>{sub}</small>}</div>;
}

function ChainStep({label,detail}:{label:string;detail:string}){
  return <div><strong>{label}</strong><small>{detail}</small></div>;
}

function SchoolMetric({label,value,attention=false}:{label:string;value:number;attention?:boolean}){
  return <div className={attention?"privacy-school-metric attention":"privacy-school-metric"}><span>{label}</span><strong>{value.toLocaleString("ko-KR")}</strong></div>;
}

function Line({label,value,attention=false}:{label:string;value:number;attention?:boolean}){
  return <div className="privacy-line"><span>{label}</span><strong className={attention?"attention":""}>{value.toLocaleString("ko-KR")}</strong></div>;
}

function Meta({label,value}:{label:string;value:string}){
  return <div><span>{label}</span><strong>{value}</strong></div>;
}
